import { useMemo, useState } from 'react';
import { Marker, Tooltip, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useTrack } from '../context/TrackContext';

const PK_STEP = 0.1; // Hectomètres

// Icône personnalisée pour les PK
const createPKIcon = (pk: number, zoom: number) => {
    const isMain = Math.round(pk * 10) % 10 === 0; // PK entier

    if (isMain) {
        const size = zoom >= 16 ? 26 : 20;
        const fontSize = 'text-[9px]';

        return L.divIcon({
            className: 'custom-pk-icon',
            html: `<div class="bg-slate-950/80 text-cyan-400 border border-cyan-500/30 rounded-full w-full h-full flex items-center justify-center font-bold ${fontSize} shadow-sm transition-all font-mono">${Math.round(pk)}</div>`,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
        });
    }

    return L.divIcon({
        className: 'custom-hm-icon',
        html: `<div class="bg-cyan-500/50 w-1 h-1 rounded-full"></div>`,
        iconSize: [4, 4],
        iconAnchor: [2, 2],
    });
};

export default function PKMarkers() {
    const { balagneSystem, centraleSystem } = useTrack();
    const map = useMapEvents({
        zoomend: () => {
            setZoom(map.getZoom());
        },
    });
    const [zoom, setZoom] = useState(map.getZoom());

    const markers = useMemo(() => {
        const calculatedMarkers: { position: [number, number], pk: number, id: string }[] = [];

        // Ligne Balagne (119.9 -> 46.7)
        let currentPK = 119.9;
        while (currentPK >= 46.7) {
            const pos = balagneSystem.pkToCoord(currentPK);
            if (pos) {
                calculatedMarkers.push({ position: pos, pk: Number(currentPK.toFixed(1)), id: `bal-${currentPK.toFixed(1)}` });
            }
            currentPK -= PK_STEP;
        }

        // Ligne Centrale (0 -> 157.4)
        currentPK = 0.0;
        while (currentPK <= 157.4) {
            const pos = centraleSystem.pkToCoord(currentPK);
            if (pos) {
                calculatedMarkers.push({ position: pos, pk: Number(currentPK.toFixed(1)), id: `cen-${currentPK.toFixed(1)}` });
            }
            currentPK += PK_STEP;
        }

        return calculatedMarkers;
    }, [balagneSystem, centraleSystem]);



    return (
        <>
            {markers.map((m) => {
                const isMain = Math.round(m.pk * 10) % 10 === 0;

                // Masquer les PK principaux à bas zoom pour éviter de saturer la carte
                if (isMain && zoom < 14) return null;
                
                // Masquer les PK hectométriques (secondaires) si le zoom est insuffisant
                if (!isMain && zoom < 16) return null;

                return (
                    <Marker
                        key={m.id}
                        position={m.position}
                        icon={createPKIcon(m.pk, zoom)}
                        zIndexOffset={isMain ? -100 : -150} // zIndexOffset ajusté pour s'afficher au-dessus du TileLayer (z-index 200)
                    >
                        {(isMain || zoom >= 17) && (
                            <Tooltip direction="top" offset={[0, isMain ? -13 : -3]} opacity={0.8} permanent={false}>
                                {isMain ? `PK ${Math.round(m.pk)}` : `.${Math.round((m.pk % 1) * 10)}`}
                            </Tooltip>
                        )}
                    </Marker>
                );
            })}
        </>
    );
}
